import {
  arrayOf,
  bool,
  elementType,
  func,
  node,
  number,
  oneOf,
  oneOfType,
  shape,
  string,
} from "prop-types";
import React, { forwardRef, useMemo } from "react";

import styled, { css } from "styled-components/macro";
import { themeVariants } from "theme";

import FnrSelectionDisplay from "FnrSelectionDisplay";

import FnrButtonWrapper from "UI/FnrButtonWrapper";
import {
  FnrPopoverOption,
  FnrPopoverOptionIcon,
  FnrPopoverOptionsList,
} from "UI/FnrPopover2";
import FnrTypography from "UI/FnrTypography/FnrTypography";

import { useOnClickOutside } from "Utilities/Hooks/useOnClickOutside";

// When the consumer supplies hoverBorderColor, the inner Selector renders
// its own focus-visible border (see FnrSelectionDisplay). Suppress the
// button's default focus chrome so that border is the sole focus indicator.
const suppressDefaultFocusStyles = css`
  &:focus-visible {
    background-color: transparent;
    outline: none;
  }
`;

const SFnrButtonWrapper = styled(FnrButtonWrapper)`
  ${({ $fullWidth }) => $fullWidth && "width: 100%;"}
  ${({ $minWidth }) => $minWidth && `min-width: ${$minWidth}px;`}
  ${({ $suppressFocusStyles }) =>
    $suppressFocusStyles && suppressDefaultFocusStyles}
`;

const dropdownListStylesByVariant = ({
  $variant,
  $optionStyle,
  theme: {
    colors: { fnBlue, white, transparent, textLight },
    shadows: { selectList },
  },
}) => {
  // popoverOption defers all chrome (bg, radius, shadow) to the wrapping
  // FnrPopover container so the dark-navy gradient + backdrop blur show
  // through. List provides only layout.
  if ($optionStyle === "popoverOption") {
    return `
      color: ${textLight};
      background-color: ${transparent};
      padding: 0;
    `;
  }
  switch ($variant) {
    case themeVariants.emphasized:
      return `
      color: ${textLight};
      background-color: ${transparent};
      padding: 0px;
      `;
    default:
      return `
        padding: 2px;
        color: ${fnBlue};
        background-color: ${white};
        ${selectList};
      `;
  }
};

// Sized to the same box as FnrPopoverOptionIcon (18px + 8px gap) so option
// labels align whether the left slot holds a check or a `leading` node.
const LeadingSlot = styled.span`
  flex-grow: 0;
  flex-shrink: 0;
  margin-right: 8px;
  width: 18px;
  text-align: center;
`;

const DropdownList = styled("ul")`
  width: max-content;
  max-width: 300px;
  min-width: ${({ $optionStyle }) =>
    $optionStyle === "popoverOption" ? "160px" : "245px"};
  border-radius: ${({ $optionStyle }) =>
    $optionStyle === "popoverOption" ? "0" : "8px"};
  max-height: ${({ $optionStyle }) =>
    $optionStyle === "popoverOption" ? "420px" : "300px"};
  overflow: auto;
  margin: 0;
  ${dropdownListStylesByVariant}

  /* popoverOption preset: keyboard focus reuses the hover background
     (ghostWhite tint) instead of the UA outline, which clashes with the
     dark navy backdrop. */
  ${({ $optionStyle, theme }) =>
    $optionStyle === "popoverOption"
      ? `& > div:focus, & > div:focus-visible {
           outline: none;
           background: ${theme.colors.ghostWhite};
           border-radius: 6px;
         }`
      : ""}
`;

const variantProps = {
  color: { emphasized: "blue", default: "white" },
  textColor: { emphasized: "textLight", default: "textSecondary" },
  hoverColor: { emphasized: "ghostWhite", default: "fnGrey" },
};

// "popoverOption" preset matches the old FnrPopoverOption-based popover that
// CountryField used before being switched to FnrSelectList2: inline left
// checkmark, lighter hover, tighter radius. Default preserves existing output
// for all other consumers.
const hoverColorForStyle = (variant, optionStyle) =>
  optionStyle === "popoverOption"
    ? "ghostWhite"
    : variantProps.hoverColor[variant];

const fnrSelectListCancelKey = "fnrSelectListCancelKey";

const getOptionNavigationIndex = ({ key, currentIndex, optionsLength }) => {
  if (!optionsLength) return null;

  switch (key) {
    case "ArrowDown":
      return (currentIndex + 1 + optionsLength) % optionsLength;
    case "ArrowUp":
      return (currentIndex - 1 + optionsLength) % optionsLength;
    case "Home":
      return 0;
    case "End":
      return optionsLength - 1;
    default:
      return null;
  }
};

/**
 * @param {object} param0
 * @param {name} param0.name - the name appended to the id. We may want to get rid of this.
 * @param {function} param0.onSelect - the function fired when a selection is made. First arg will be the currently selected option(s)
 * @param {[SelectListOption]} param0.options - the options the will appear in the dropdown.
 * @param {SelectListOption} param0.selected - the option currently selected. Passing this in overrides local state.
 * @param {"default"|"emphasized"} param0.variant - set to emphasized to add some OOMF. TODO: make this a boolean: emphasized.
 * @param {boolean} param0.fullWidth - Allows the ButtonComponent to take up the full width.
 * @param {boolean} param0.multiselect - Allows multiple options to be selected.
 * @param {number} param0.minWidth - minimum width of container in px.
 * @param {import("react").ElementType} param0.ButtonComponent - the component used for clicking. Defaults to FnrSelectionDisplay. Takes in ...rest.
 * @param {boolean} param0.throttleProps - set to true to prevent generated props from going to ButtonComponent (selectedId, multiselect, variant, options, and active)
 * @param {function} param0.onOpenDropdown - if provided, is fired when the dropdown opens
 * @param {function} param0.onCloseDropdown - if provided, is fired when the dropdown closes
 * @param {import("react").ElementType} param0.OptionComponent - optional custom component for rendering each dropdown option. Receives `option` prop.
 * @param {string} param0.hoverBorderColor - optional color (theme key or CSS value) applied to the Selector border on hover and on keyboard focus of the wrapping button.
 * @param {number} param0.triggerTabIndex - tabIndex for the wrapping trigger <button>. Pass -1 to keep it pointer-clickable but out of the keyboard Tab sequence. Omit to leave the default tab order.
 * @returns
 */
const FnrSelectList2 = forwardRef(
  (
    {
      name,
      onSelect,
      options,
      selected: selectedArg,
      variant = "default",
      fullWidth,
      multiselect,
      minWidth,
      ButtonComponent = FnrSelectionDisplay,
      throttleProps,
      onOpenDropdown,
      onCloseDropdown,
      dataCancelClickAway = fnrSelectListCancelKey,
      optionStyle = "default",
      OptionComponent,
      hoverBorderColor,
      triggerTabIndex,
      ...rest
    },
    ref
  ) => {
    const dropdownRef = React.useRef();
    const listRef = React.useRef();
    const localRef = React.useRef();
    const containerRef = ref || localRef;
    const [showDropdown, setShowDropdown] = React.useState(false);
    const showDropdownRef = React.useRef(false);
    showDropdownRef.current = showDropdown;
    const [selected, setSelected] = React.useState();
    const optionRefs = React.useRef([]);

    const selectedId = useMemo(() => {
      let localSelectedArg = selectedArg || selected;
      if (multiselect && !Array.isArray(localSelectedArg))
        localSelectedArg = localSelectedArg ? [localSelectedArg] : [];
      if (multiselect) return localSelectedArg.map(({ id }) => id);
      return localSelectedArg?.id || selected?.id;
    }, [multiselect, selectedArg, selected]);

    useOnClickOutside(
      containerRef,
      (e) => {
        if (showDropdown) {
          setShowDropdown(false);
          if (onCloseDropdown) onCloseDropdown();
        }
      },
      dataCancelClickAway
    );

    const toggleDropdown = () => {
      setShowDropdown((dropdown) => {
        if (dropdown && onCloseDropdown) onCloseDropdown();
        else if (onOpenDropdown) onOpenDropdown();
        return !dropdown;
      });
    };

    const handleSelection = (e, option) => {
      e.stopPropagation();
      if (option.disabled) return;

      if (multiselect) {
        // if the value is already selected, the deselect the value.
        const newIdArray = [...selectedId];
        if (!newIdArray.includes(option.id)) {
          newIdArray.push(option.id);
        } else {
          newIdArray.splice(newIdArray.indexOf(option.id), 1);
        }
        const newValue = newIdArray.map((currentId) =>
          options.find(({ id }) => id === currentId)
        );
        setSelected(newValue);
        if (onSelect) {
          onSelect(newValue);
        }
      } else {
        setSelected(option);
        setShowDropdown(false);
        containerRef.current?.focus();

        if (onSelect) {
          onSelect(option);
        }
      }
    };

    const focusOptionByIndex = (index) => {
      const option = optionRefs.current[index];
      if (!option) return;
      option.focus();
      option.scrollIntoView({ block: "nearest" });
    };

    const focusFirstVisibleOption = () => {
      // AnimatePresence preserves stale props during exit animations,
      // so this callback fires for both entry and exit. Use a ref to
      // check the current dropdown state and skip the exit case.
      if (!showDropdownRef.current) return false;

      const firstVisibleOption = optionRefs.current.find(
        (optionNode) =>
          optionNode &&
          optionNode.closest &&
          optionNode.closest('[data-cy="FnrPopover_Container"]')
      );

      if (!firstVisibleOption) return false;

      firstVisibleOption.focus();
      firstVisibleOption.scrollIntoView({ block: "nearest" });
      return true;
    };

    // Handles the keyboard navigation for the options.
    const handleOptionKeyDown = (e, optionIndex, option) => {
      const nextIndex = getOptionNavigationIndex({
        key: e.key,
        currentIndex: optionIndex,
        optionsLength: options.length,
      });

      if (nextIndex !== null) {
        e.preventDefault();
        e.stopPropagation();
        focusOptionByIndex(nextIndex);
        return;
      }

      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        handleSelection(e, option);
        return;
      }

      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        setShowDropdown(false);
        if (onCloseDropdown) onCloseDropdown();
      }
    };

    const buttonComponentProps = useMemo(() => {
      if (throttleProps) return rest;
      return {
        selectedId,
        multiselect,
        variant,
        options,
        active: showDropdown,
        hoverBorderColor,
        ...rest,
      };
    }, [
      hoverBorderColor,
      multiselect,
      options,
      rest,
      selectedId,
      showDropdown,
      throttleProps,
      variant,
    ]);

    // Trigger-focus Escape: the option-level handler only fires once focus has
    // moved into the list, so a user who clicks the trigger to open and then
    // hits Escape needs this wrapper-level fallback.
    const handleContainerKeyDown = (e) => {
      if (e.key === "Escape" && showDropdownRef.current) {
        e.preventDefault();
        setShowDropdown(false);
        if (onCloseDropdown) onCloseDropdown();
      }
    };

    // A disabled option drops its own click handler, so the click bubbles up
    // here through the portal and would toggle the list shut.
    const handleWrapperClick = (e) => {
      if (listRef.current?.contains(e.target)) return;
      toggleDropdown();
    };

    const isPopoverStyle = optionStyle === "popoverOption";

    return (
      <SFnrButtonWrapper
        $minWidth={minWidth}
        ref={containerRef}
        $fullWidth={fullWidth}
        $suppressFocusStyles={Boolean(hoverBorderColor)}
        type="button"
        tabIndex={triggerTabIndex}
        onClick={handleWrapperClick}
        onKeyDown={handleContainerKeyDown}
      >
        <ButtonComponent
          id={`FnrSelectList2_${name}`}
          ref={dropdownRef}
          {...buttonComponentProps}
        />
        <FnrPopoverOptionsList
          show={showDropdown}
          buttonRef={containerRef}
          color={isPopoverStyle ? undefined : variantProps.color[variant]}
          background={hoverColorForStyle(variant, optionStyle)}
          dataCancelClickAway={dataCancelClickAway}
          onAnimationComplete={focusFirstVisibleOption}
        >
          <DropdownList
            ref={listRef}
            role="listbox"
            $variant={variant}
            $optionStyle={optionStyle}
          >
            {options.map((option, index) => {
              const isSelected = Array.isArray(selectedId)
                ? selectedId.includes(option?.id)
                : selectedId === option?.id;
              return (
                <FnrPopoverOption
                  key={option.id}
                  ref={(node) => {
                    optionRefs.current[index] = node;
                  }}
                  onClick={(e) => {
                    handleSelection(e, option);
                  }}
                  onKeyDown={(e) => handleOptionKeyDown(e, index, option)}
                  tabIndex={-1}
                  role="option"
                  disabled={option.disabled}
                  loading={option.loading}
                  aria-disabled={option.disabled || undefined}
                  aria-selected={isSelected}
                  // popoverOption renders its own inline left checkmark, so
                  // suppress FnrPopoverOption's animated right-side check —
                  // unless the option supplies a `leading` node (e.g. a
                  // country flag), which takes over the left slot and pushes
                  // the selection check to FnrPopoverOption's right side.
                  selected={
                    isPopoverStyle
                      ? Boolean(option.leading) && isSelected
                      : isSelected
                  }
                >
                  {isPopoverStyle ? (
                    <>
                      {option.leading ? (
                        <LeadingSlot>{option.leading}</LeadingSlot>
                      ) : (
                        <FnrPopoverOptionIcon
                          icon={isSelected ? "check fas" : ""}
                        />
                      )}
                      {option.label}
                    </>
                  ) : OptionComponent ? (
                    <OptionComponent option={option} />
                  ) : (
                    <FnrTypography
                      color={variantProps.textColor[variant]}
                      yMarginAdjust={2}
                    >
                      {option.label}
                    </FnrTypography>
                  )}
                </FnrPopoverOption>
              );
            })}
          </DropdownList>
        </FnrPopoverOptionsList>
      </SFnrButtonWrapper>
    );
  }
);

const optionShape = shape({
  id: oneOfType([string, number]).isRequired,
  label: string,
  // Optional node rendered in the option's left slot (popoverOption style
  // only), e.g. a country flag. Displaces the inline left checkmark, which
  // moves to the right side.
  leading: node,
  disabled: bool,
  // Swaps the option's checkmark slot for bricks while something about it is
  // still saving.
  loading: bool,
});

FnrSelectList2.propTypes = {
  name: string.isRequired,
  options: arrayOf(optionShape).isRequired,
  onSelect: func,
  selected: oneOfType([optionShape, arrayOf(optionShape)]),
  variant: oneOf(["default", "emphasized"]),
  fullWidth: bool,
  multiselect: bool,
  minWidth: number,
  throttleProps: bool,
  ButtonComponent: elementType,
  OptionComponent: elementType,
  onOpenDropdown: func,
  onCloseDropdown: func,
  dataCancelClickAway: string,
  optionStyle: oneOf(["default", "popoverOption"]),
  hoverBorderColor: string,
  triggerTabIndex: number,
};

export default FnrSelectList2;
